import { createTeamInvite, type TeamInviteIssuedView } from '@api/teamInvite';
import useToaster from '@hooks/toaster/useToaster';
import {
    Button,
    CopyButton,
    Group,
    Modal,
    Stack,
    Text,
    TextInput,
} from '@mantine/core';
import { ButtonVariant } from '@providers/Mantine/mantineTheme';
import { useState, type FC } from 'react';

import styles from './UsersTab.module.css';

interface InviteUserModalProps {
    opened: boolean;
    onClose: () => void;
    /** Called once an invite went out, so the list behind the modal can refresh. */
    onSent: () => void;
}

/**
 * Invite a colleague to the brand.
 *
 * THE SAME SHAPE AS THE CM SITES INVITE, minus the site: there is nothing to pick, because
 * the one decision that matters (which brand) is already made by who is signed in. The
 * login created is an admin of this brand.
 *
 * THE LINK STAYS ON SCREEN AFTER SENDING, prominent when the mail failed and quiet when it
 * went, because email is the least reliable part of this chain and a colleague who never got
 * the message needs the admin to hand the link over some other way.
 */
const InviteUserModal: FC<InviteUserModalProps> = ({ opened, onClose, onSent }) => {
    const { showToastError } = useToaster();
    const [email, setEmail] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [issued, setIssued] = useState<TeamInviteIssuedView | null>(null);

    const reset = () => {
        setEmail('');
        setFirstName('');
        setLastName('');
        setIssued(null);
        setIsSending(false);
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    const handleSend = async () => {
        setIsSending(true);
        try {
            const result = await createTeamInvite({
                email: email.trim(),
                firstName: firstName.trim() || undefined,
                lastName: lastName.trim() || undefined,
            });
            setIssued(result);
            onSent();
        } catch (err: any) {
            showToastError({
                title: 'Could not send the invite',
                subtitle: err?.error?.message ?? err?.message ?? 'Something went wrong.',
            });
        } finally {
            setIsSending(false);
        }
    };

    return (
        <Modal opened={opened} onClose={handleClose} title="Invite a user" size="md" centered>
            {issued ? (
                <Stack gap="md">
                    <Text size="sm">
                        {issued.emailed
                            ? `${issued.reused ? 'Re-sent the invite' : 'Invite sent'} to ${issued.email}. `
                              + 'They have 7 days to set up their login.'
                            : `The invite for ${issued.email} was created, but the email could `
                              + 'not be sent. Share this link with them yourself:'}
                    </Text>

                    {/* Kept visible even when mailed - "it never arrived" is a support call
                        this box answers without another round trip. */}
                    <Group gap={8} wrap="nowrap">
                        <TextInput value={issued.link} readOnly className={styles.linkField} />
                        <CopyButton value={issued.link}>
                            {({ copied, copy }) => (
                                <Button variant={ButtonVariant.DEFAULT} onClick={copy}>
                                    {copied ? 'Copied' : 'Copy'}
                                </Button>
                            )}
                        </CopyButton>
                    </Group>

                    <Group justify="flex-end">
                        <Button onClick={handleClose}>Done</Button>
                    </Group>
                </Stack>
            ) : (
                <Stack gap="md">
                    <Text size="sm" c="dimmed">
                        They&apos;ll get a link to choose a password. The login is an admin of
                        this brand and sees everything you see.
                    </Text>

                    <TextInput
                        label="Email"
                        placeholder="colleague@yourbrand.example"
                        value={email}
                        onChange={(e) => setEmail(e.currentTarget.value)}
                        withAsterisk
                        data-autofocus
                    />

                    <Group grow>
                        <TextInput
                            label="First name"
                            value={firstName}
                            onChange={(e) => setFirstName(e.currentTarget.value)}
                        />
                        <TextInput
                            label="Last name"
                            value={lastName}
                            onChange={(e) => setLastName(e.currentTarget.value)}
                        />
                    </Group>

                    <Group justify="flex-end" gap={8}>
                        <Button variant={ButtonVariant.DEFAULT} onClick={handleClose}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSend}
                            loading={isSending}
                            disabled={!/^\S+@\S+\.\S+$/.test(email.trim())}
                        >
                            Send invite
                        </Button>
                    </Group>
                </Stack>
            )}
        </Modal>
    );
};

export default InviteUserModal;
